import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, Settings2, XCircle, RefreshCw } from "lucide-react";
import {
  listGlobalCatalog,
  listEnablements,
  disableGlobalBenefit,
} from "../../../../../Services/adminBenefits";
import { Button } from "../../../../../ui/button";
import { Card, CardContent } from "../../../../../ui/card";
import { Badge } from "../../../../../ui/badge";
import { Skeleton } from "../../../../../ui/skeleton";
import EmptyState from "../../../../../Components/admin/shared/EmptyState";
import ConfirmDialog from "../../../../../Components/admin/shared/ConfirmDialog";
import GlobalOverrideDialog from "./GlobalOverrideDialog";
import { extractApiError, humanEnum } from "./utils";

const GlobalCatalogSection = () => {
  const { id: corporateId } = useParams();
  const [globals, setGlobals] = useState([]);
  const [enablementsByGid, setEnablementsByGid] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const [overrideTarget, setOverrideTarget] = useState(null);
  const [disableTarget, setDisableTarget] = useState(null);
  const [actionError, setActionError] = useState(null);

  const load = useCallback(async () => {
    if (!corporateId) return;
    setLoading(true);
    setLoadError(null);
    try {
      const [catalog, enablements] = await Promise.all([
        listGlobalCatalog(corporateId, { status: "active" }),
        listEnablements(corporateId),
      ]);
      setGlobals(catalog?.items || []);
      const map = {};
      (enablements?.items || []).forEach((en) => {
        if (en?.global_benefit_id) {
          map[en.global_benefit_id] = en;
        }
      });
      setEnablementsByGid(map);
    } catch (err) {
      setLoadError(extractApiError(err, "Failed to load global catalog."));
    } finally {
      setLoading(false);
    }
  }, [corporateId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleDisable = async () => {
    if (!disableTarget) return;
    setActionError(null);
    try {
      await disableGlobalBenefit(corporateId, disableTarget.id);
      setDisableTarget(null);
      load();
    } catch (err) {
      setActionError(extractApiError(err, "Failed to disable benefit."));
    }
  };

  const summary = useMemo(() => {
    const enabled = Object.values(enablementsByGid).filter(
      (en) => en.is_enabled
    ).length;
    return { enabled, total: globals.length };
  }, [enablementsByGid, globals.length]);

  return (
    <section className="space-y-4">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">Available global benefits</h2>
          <p className="text-sm text-muted-foreground">
            Platform-authored benefits — opt in with optional overrides.
            Identity (name, signals, follow-ups) is locked; only contact and
            tone fields are overridable.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-muted-foreground">
            {summary.enabled} of {summary.total} enabled
          </span>
          <Button variant="outline" size="icon" onClick={load} aria-label="Refresh">
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {actionError && (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
          {actionError}
        </div>
      )}

      {loadError && (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
          {loadError}
        </div>
      )}

      {loading ? (
        <div className="grid md:grid-cols-2 gap-3">
          {Array.from({ length: 2 }).map((_, i) => (
            <Card key={i}>
              <CardContent className="pt-4 space-y-2">
                <Skeleton className="h-5 w-2/3" />
                <Skeleton className="h-4 w-1/2" />
                <Skeleton className="h-4 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : globals.length === 0 ? (
        <EmptyState
          title="No global benefits available"
          description="The platform team hasn't published any active global benefits yet. They will appear here once available."
        />
      ) : (
        <div className="grid md:grid-cols-2 gap-3">
          {globals.map((gb) => {
            const enablement = enablementsByGid[gb.id];
            const isEnabled = !!enablement?.is_enabled;
            return (
              <Card key={gb.id}>
                <CardContent className="pt-4 space-y-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <h3 className="text-sm font-semibold leading-tight">
                        {gb.name}
                      </h3>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {gb.provider} · {humanEnum(gb.category)}
                      </p>
                    </div>
                    {isEnabled ? (
                      <Badge variant="success">Enabled</Badge>
                    ) : (
                      <Badge variant="outline">Not enabled</Badge>
                    )}
                  </div>

                  {gb.helps_with && (
                    <p className="text-xs text-muted-foreground line-clamp-3">
                      {gb.helps_with}
                    </p>
                  )}

                  {Array.isArray(gb.signals) && gb.signals.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {gb.signals.slice(0, 4).map((s) => (
                        <span
                          key={s.id}
                          className="inline-flex items-center rounded-full bg-primary/10 text-primary px-2 py-0.5 text-[11px]"
                        >
                          {s.name}
                        </span>
                      ))}
                      {gb.signals.length > 4 && (
                        <span className="text-[11px] text-muted-foreground">
                          +{gb.signals.length - 4} more
                        </span>
                      )}
                    </div>
                  )}

                  <div className="flex items-center justify-end gap-2 pt-1">
                    {isEnabled ? (
                      <>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            setOverrideTarget({ global: gb, enablement })
                          }
                        >
                          <Settings2 className="h-3.5 w-3.5 mr-1.5" />
                          Overrides
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => setDisableTarget(gb)}
                          className="text-destructive hover:text-destructive"
                        >
                          <XCircle className="h-3.5 w-3.5 mr-1.5" /> Disable
                        </Button>
                      </>
                    ) : (
                      <Button
                        type="button"
                        size="sm"
                        onClick={() =>
                          setOverrideTarget({ global: gb, enablement: null })
                        }
                      >
                        <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />
                        Enable for this corporate
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <GlobalOverrideDialog
        open={!!overrideTarget}
        onOpenChange={(open) => !open && setOverrideTarget(null)}
        globalBenefit={overrideTarget?.global || null}
        enablement={overrideTarget?.enablement || null}
        onSaved={() => {
          setOverrideTarget(null);
          load();
        }}
      />

      <ConfirmDialog
        open={!!disableTarget}
        onOpenChange={(open) => !open && setDisableTarget(null)}
        title="Disable this global benefit?"
        description={
          disableTarget
            ? `Members of this corporate will no longer see "${disableTarget.name}". Your overrides are preserved if you re-enable later.`
            : ""
        }
        confirmLabel="Disable"
        destructive
        onConfirm={handleDisable}
      />
    </section>
  );
};

export default GlobalCatalogSection;
